import React, { useState, useEffect, useCallback } from 'react';
import { feedbackAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './Feedback.css';

const STAR_DISPLAY = (rating) => '\u2605'.repeat(rating) + '\u2606'.repeat(5 - rating);

const Feedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({ total: 0, avg_rating: 0, five_star: 0, four_star: 0, three_star: 0, two_star: 0, one_star: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [ratingFilter, setRatingFilter] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await feedbackAPI.getStats();
      setStats(res.data.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const res = await feedbackAPI.getAll({ page, limit: 20, rating: ratingFilter, search });
      setFeedback(res.data.data.feedback);
      setTotalPages(res.data.data.pagination.totalPages);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [page, ratingFilter, search]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await feedbackAPI.delete(id);
      setToast({ type: 'success', message: 'Feedback deleted' });
      fetchFeedback();
      fetchStats();
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to delete' });
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="fb-page">
          <h1 className="fb-title">Beta Feedback</h1>

          {/* Stats */}
          <div className="fb-stats">
            <div className="fb-stat-card">
              <span className="fb-stat-value">{stats.total}</span>
              <span className="fb-stat-label">Total</span>
            </div>
            <div className="fb-stat-card fb-stat-avg">
              <span className="fb-stat-value">{stats.avg_rating || '0.0'}</span>
              <span className="fb-stat-label">Avg Rating</span>
            </div>
            <div className="fb-stat-card fb-stat-five">
              <span className="fb-stat-value">{stats.five_star || 0}</span>
              <span className="fb-stat-label">5-Star</span>
            </div>
            <div className="fb-stat-card fb-stat-one">
              <span className="fb-stat-value">{stats.one_star || 0}</span>
              <span className="fb-stat-label">1-Star</span>
            </div>
          </div>

          {/* Filters */}
          <div className="fb-filters">
            <select
              value={ratingFilter}
              onChange={(e) => { setRatingFilter(e.target.value); setPage(1); }}
              className="fb-filter-select"
            >
              <option value="">All Ratings</option>
              {[5, 4, 3, 2, 1].map(r => (
                <option key={r} value={r}>{r} Star{r > 1 ? 's' : ''}</option>
              ))}
            </select>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search description or email..."
              className="fb-filter-search"
            />
          </div>

          {/* Table */}
          <div className="fb-table-wrap">
            <table className="fb-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Rating</th>
                  <th>Description</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="fb-table-empty">Loading...</td></tr>
                ) : feedback.length === 0 ? (
                  <tr><td colSpan={5} className="fb-table-empty">No feedback yet</td></tr>
                ) : feedback.map((fb) => (
                  <tr key={fb.id}>
                    <td>{fb.user_email || 'Anonymous'}</td>
                    <td className="fb-rating-cell">
                      <span className="fb-stars">{STAR_DISPLAY(fb.rating)}</span>
                    </td>
                    <td className="fb-desc-cell">{fb.description || '-'}</td>
                    <td className="fb-date-cell">{formatDate(fb.created_at)}</td>
                    <td className="fb-actions-cell">
                      <button className="fb-view-btn" onClick={() => setViewItem(fb)} title="View full feedback">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                      <button className="fb-delete-btn" onClick={() => handleDelete(fb.id)} title="Delete">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="fb-pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div className={`fb-toast fb-toast-${toast.type}`} onClick={() => setToast(null)}>
            {toast.message}
          </div>
        )}
      </main>

      {/* View Modal */}
      {viewItem && (
        <div className="fb-modal-overlay" onClick={() => setViewItem(null)}>
          <div className="fb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fb-modal-header">
              <span className="fb-modal-title">Feedback</span>
              <button className="fb-modal-close" onClick={() => setViewItem(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="fb-modal-body">
              <div className="fb-modal-row">
                <span className="fb-modal-label">User</span>
                <span className="fb-modal-value">{viewItem.user_email || 'Anonymous'}</span>
              </div>
              <div className="fb-modal-row">
                <span className="fb-modal-label">Rating</span>
                <span className="fb-stars fb-modal-value">{STAR_DISPLAY(viewItem.rating)}</span>
              </div>
              <div className="fb-modal-row">
                <span className="fb-modal-label">Date</span>
                <span className="fb-modal-value">{formatDate(viewItem.created_at)}</span>
              </div>
              <div className="fb-modal-desc-block">
                <span className="fb-modal-label">Description</span>
                <p className="fb-modal-desc">{viewItem.description || 'No description provided.'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;
